/* global graphql */

import React, { useEffect, useRef } from "react";

import { compose, withHandlers, withState, withProps } from "recompose";

import { Box, Flex, Image, Link, Text } from "src/components";
import { defaultPrevented } from "src/utils";

import SearchIcon from "react-icons/lib/fa/search";
import FaCommentingO from "react-icons/lib/fa/commenting-o";

import LoginDropdown from "./LoginDropdown";

import { colors } from "src/styles";

import Menu from "./Menu";

import MenuOpenIcon from "react-icons/lib/fa/bars";
import MenuCloseIcon from "react-icons/lib/fa/close";
import MenuCloseHoverIcon from "react-icons/lib/fa/times-circle";

import InlineSearchForm from "src/search/InlineSearchForm";

const enhanceMenuIcon = compose(
  withState("hovered", "setHover", false),
  withHandlers({
    onMouseEnter: ({ setHover }) => () => setHover(true),
    onMouseLeave: ({ setHover }) => () => setHover(false),
  }),
  withProps(({ hovered, menuVisible }) => ({
    Component: menuVisible
      ? hovered
        ? MenuCloseHoverIcon
        : MenuCloseIcon
      : MenuOpenIcon,
  }))
);

const MenuLink = enhanceMenuIcon(
  ({ Component, onMouseEnter, onMouseLeave, toggleMenu, ...props }) => (
    <Link
      onClick={toggleMenu}
      to="#"
      mr={[2, 2, 0]}
      aria-label="Toggle menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Component color={colors.orange} size={30} />
    </Link>
  )
);

const LoggedInUser = ({ setLoginVisible }) => (
  <Flex mr={[2, 2, 3]} flexDirection="column" align="flex-end">
    <Link
      fontSize={2}
      fontWeight="bold"
      color="blue"
      inline={true}
      to="/app"
      onClick={defaultPrevented(() => setLoginVisible(true))}
    >
      Matt Dean
    </Link>
  </Flex>
);

const Header = ({
  data,
  t,
  menuVisible,
  toggleMenu,
  toggleLogin,
  toggleSearch,
  loginVisible,
  searchExpanded,
  setLoginVisible,
  loggedIn,
  logOut,
  locale,
}) => {
  const stickyRef = useRef();

  useEffect(() => {
    // Stickyfill depends on `window`, which isn't present during server-side
    // render. Moving this `require` to the `useEffect` prevents it from being
    // loaded until it's used in the browser.
    const Stickyfill = require("stickyfilljs");
    const el = stickyRef.current;
    Stickyfill.addOne(el);
    return () => Stickyfill.removeOne(el);
  }, []);

  //Check URL param to see if live chat should be open
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const liveChat = urlParams.get("liveChat");

    // If liveChat is in the URL, open the chat. Try again every 2 seconds until it opens
    // There is a load delay for the chat window
    if (liveChat) {
      var result = handleLiveChatClick();
      if (!result) {
        const chatInterval = setInterval(() => {
          var result = handleLiveChatClick();
          if (result) {
            clearInterval(chatInterval);
          }
        }, 2000);
      }
    }
  }, []);

  const handleLiveChatClick = () => {
    // Get div element with class livesdk__invitation
    const liveChatButton = document.querySelector(".livesdk__invitation");
    if (liveChatButton) {
      // Check that the div is not already open
      // Check with chatWindow which is hidden when open
      const chatWindow = document.querySelector(".livesdk__placement");
      if (chatWindow && chatWindow.style.display !== "none") {
        liveChatButton.click();
        return true;
      }
    }
    return false;
  };

  return (
    <Box className="sticky" innerRef={stickyRef}>
      <Flex
        bg="white"
        py={2}
        px={[2, 3, 4, 6]}
        justify="space-between"
        align="center"
        flexDirection={["column", null, "row"]}
        printStyles={{ display: "none" }}
      >
        <Link
          to="/"
          mx={2}
          aria-label="Return to the home page"
          style={{ width: 275 }}
        >
          {data && data.logo && (
            <Image sizes={data.logo.sizes} alt="Financial Center Logo" />
          )}
        </Link>
        <Flex
          mt={[2, null, 0]}
          align="center"
          printStyles={{ display: "none" }}
        >
          <Box
            display={
              searchExpanded ? ["none", null, null, null, "block"] : "block"
            }
          >
            <Flex>
              <Link
                bg="blue"
                borderRadius={4}
                color="white"
                style={{
                  lineHeight: "1em",
                }}
                textDecoration="none"
                align="center"
                justify="center"
                mr={2}
                py={1}
                px={[2, 3]}
                fontWeight="semibold"
                fontSize={15}
                data-testid="branch-appointment-button"
                href="https://fcfcu.makeplans.net/"
                target="_blank"
              >
                {t("button.branchAppointment", {
                  defaultValue: "Branch Appointment",
                })}
              </Link>
              {false && loggedIn ? (
                <LoggedInUser setLoginVisible={setLoginVisible} />
              ) : (
                <Link
                  bg="green"
                  borderRadius={4}
                  color="white"
                  py={1}
                  style={{
                    lineHeight: "1em",
                  }}
                  textDecoration="none"
                  align="center"
                  justify="center"
                  mr={2}
                  px={[2, 3]}
                  fontWeight="semibold"
                  fontSize={15}
                  data-testid="login-button"
                  to="//online.fcfcu.com"
                  // onClick={defaultPrevented(() => setLoginVisible(true))}
                >
                  {t("button.onlineBanking")}
                </Link>
              )}
            </Flex>
          </Box>
          <Box
            display={
              searchExpanded ? ["none", null, null, null, "block"] : "block"
            }
          >
            <Flex mr={2}>
              <Box style={{ cursor: "pointer" }} onClick={handleLiveChatClick}>
                <FaCommentingO size={30} color={colors.orange} />
              </Box>
            </Flex>
          </Box>
          <Flex flexDirection="row" align="center">
            {searchExpanded ? (
              <Flex minWidth={300} mr={2}>
                <InlineSearchForm onClose={toggleSearch} />
              </Flex>
            ) : (
              <Link to="#" mr={2} aria-label="Search" onClick={toggleSearch}>
                <SearchIcon size={26} color={colors.orange} />
              </Link>
            )}
            <Box
              display={
                searchExpanded ? ["none", null, null, null, "block"] : "block"
              }
            >
              <MenuLink toggleMenu={toggleMenu} menuVisible={menuVisible} />
            </Box>
          </Flex>
        </Flex>
      </Flex>
      {data && data.menu ? (
        <Box printStyles={{ display: "none" }}>
          <Menu
            menuVisible={menuVisible}
            menu={data.menu}
            menuLinks={data.menuLinks}
            t={t}
            locale={locale}
          />
        </Box>
      ) : null}
      {loginVisible && <LoginDropdown loggedIn={loggedIn} logOut={logOut} />}
    </Box>
  );
};

const enhance = compose(
  withState("menuVisible", "setMenuVisible", false),
  withState("searchExpanded", "setSearchExpanded", false),
  withHandlers({
    toggleMenu: props => e => {
      e.preventDefault();
      props.setMenuVisible(!props.menuVisible);
    },
    toggleLogin: props => e => {
      e.preventDefault();
      props.setLoginVisible(!props.loginVisible);
    },
    toggleSearch: props => e => {
      e && e.preventDefault();
      props.setSearchExpanded(!props.searchExpanded);
    },
  })
);

export default enhance(Header);



// WEBPACK FOOTER //
// ./src/components/Layout/Header.js